import { useState, useRef } from 'react';
import { useAdaptivePoll } from '@shared/hooks/useAdaptivePoll';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { HardDrive, X } from 'lucide-react';
import { Button } from './ui/Button';
import { getStorageTone } from '@shared/config/theme';
import {
  toneChipClasses,
  toneIconBgClasses,
  toneDotClasses,
  toneTextClasses,
  focusRing,
} from '@shared/utils/statusTone';

interface StorageStatus {
  usedGB: number;
  limitGB: number;
  remainingGB: number;
  percentage: number;
  status: 'healthy' | 'warning' | 'critical' | 'exceeded' | 'hard_limit' | 'unknown';
  gracePeriodEnd?: string;
  backupCount: number;
  canCreateBackup: boolean;
  vaults?: Array<{
    destination_id: string;
    name: string;
    type: string;
    used_gb: number;
  }>;
}

export default function StorageBanner() {
  const { t, i18n } = useTranslation('storage');
  const navigate = useNavigate();
  const [storage, setStorage] = useState<StorageStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  // Read inside the adaptive poll's fetcher so the poll is not recreated on
  // every response.
  const storageRef = useRef<string>('');
  const fetchStorageStatusRef = useRef<() => Promise<void>>(async () => {});

  const fetchStorageStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/v1/billing/storage-status', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setStorage(data);
      }
    } catch (err) {
      console.error('Failed to fetch storage status:', err);
    } finally {
      setLoading(false);
    }
  };

  storageRef.current = storage ? `${storage.status}:${storage.percentage}` : '';
  fetchStorageStatusRef.current = fetchStorageStatus;

  // Storage usage changes slowly and this is the only consumer of the endpoint,
  // so there is no dedupe win — but the poll must not run in background tabs.
  // Adaptive: 5min base, stretching to 30min once the figure stops moving.
  useAdaptivePoll({
    enabled: true,
    baseIntervalMs: 5 * 60 * 1000,
    maxIntervalMs: 30 * 60 * 1000,
    fetcher: async () => {
      await fetchStorageStatusRef.current();
      return storageRef.current;
    },
    isEqual: (a, b) => a === b,
  });

  // Don't show if loading, no data, healthy status, or manually dismissed
  if (
    loading ||
    !storage ||
    storage.status === 'healthy' ||
    storage.status === 'unknown' ||
    dismissed
  ) {
    return null;
  }

  // Don't allow dismiss for critical states
  const canDismiss = storage.status === 'warning';
  const locale = i18n.resolvedLanguage || i18n.language;
  const formatNumber = (value: number, maximumFractionDigits = 1) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value);

  const getBannerConfig = () => {
    // Colour slots come from the shared tone contract (`shared/utils/statusTone.ts`);
    // each threshold below only supplies copy, CTA and the upgrade hint.
    const tone = getStorageTone(storage.status);
    const base = {
      // `toneIconBgClasses` = tone surface + foreground, without a border (the
      // banner's own edge is set below). `toneChipClasses` is not used for the
      // banner surface because its bundled `/20` border would fight that edge.
      bgColor: toneIconBgClasses[tone],
      iconColor: toneTextClasses[tone],
      iconBgColor: toneChipClasses[tone],
    };

    switch (storage.status) {
      case 'warning':
        return {
          ...base,
          title: t('banner.warning.title'),
          message: t('banner.warning.message', {
            percent: formatNumber(storage.percentage, 0),
            used: formatNumber(storage.usedGB),
            limit: formatNumber(storage.limitGB),
          }),
          action: { label: t('banner.warning.action'), href: '/storage-breakdown' },
        };
      case 'critical':
        return {
          ...base,
          title: t('banner.critical.title'),
          message: t('banner.critical.message', {
            percent: formatNumber(storage.percentage, 0),
          }),
          action: { label: t('banner.critical.action'), href: '/billing' },
        };
      case 'exceeded':
        return {
          ...base,
          title: t('banner.exceeded.title'),
          message: storage.gracePeriodEnd
            ? t('banner.exceeded.messageWithGrace', {
                date: new Date(storage.gracePeriodEnd).toLocaleDateString(locale),
              })
            : t('banner.exceeded.message', { limit: formatNumber(storage.limitGB) }),
          action: { label: t('banner.exceeded.action'), href: '/billing' },
        };
      case 'hard_limit':
        return {
          ...base,
          title: t('banner.hardLimit.title'),
          message: t('banner.hardLimit.message'),
          action: { label: t('banner.hardLimit.action'), href: '/billing' },
        };
      default:
        return null;
    }
  };

  const config = getBannerConfig();
  if (!config) return null;
  return (
    <div
      id="storage-banner"
      className={`${config.bgColor} border-b border-surface-border-light dark:border-surface-border-dark`}
    >
      <div className="mx-auto max-w-7xl px-4 py-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3 sm:items-center">
            <div className={`shrink-0 rounded-lg p-2 ${config.iconBgColor}`}>
              <HardDrive size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                <span className={`font-semibold text-sm ${config.iconColor}`}>{config.title}</span>
                <span className="text-slate-600 dark:text-slate-300 text-sm">{config.message}</span>
              </div>
              {/* Progress bar */}
              <div className="mt-1.5 h-1.5 w-48 max-w-full overflow-hidden rounded-full bg-surface-border-light dark:bg-surface-border-dark">
                <div
                  className={`h-full rounded-full ${
                    storage.percentage >= 100 ? toneDotClasses.danger : toneDotClasses.warn
                  }`}
                  style={{ width: `${Math.min(storage.percentage, 100)}%` }}
                />
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-2">
            <Button
              size="sm"
              variant={storage.status === 'warning' ? 'secondary' : 'primary'}
              onClick={() => navigate(config.action.href)}
            >
              {config.action.label}
            </Button>

            {canDismiss && (
              <button
                type="button"
                onClick={() => setDismissed(true)}
                aria-label={t('actions.dismiss')}
                title={t('actions.dismiss')}
                className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-text-subtle-light transition-colors hover:bg-surface-2-light hover:text-slate-700 dark:text-text-subtle-dark dark:hover:bg-surface-2-dark dark:hover:text-slate-200 ${focusRing}`}
              >
                <X size={18} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
